import React from 'react';
import { Card, Timeline, Spin, Tag, Button, Tooltip, Image } from 'antd';
import {
  ClockCircleOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { AttachmentItem } from '../CycleLogAttachments';
import {
  getLogColor,
  getLogIcon,
  getLogTypeLabel,
  formatDateTime,
  getResolvedElementInfo,
} from './cycleDetailHelpers';

export interface CycleDetailTimelineTabProps {
  logs: any[];
  cycle: any;
  itemsMap: Map<string, any>;
  isLoadingLogs: boolean;
  isMobile?: boolean;
  filterBar: React.ReactNode;
  onSelectLog: (log: any) => void;
}

export const CycleDetailTimelineTab: React.FC<CycleDetailTimelineTabProps> = ({
  logs,
  cycle,
  itemsMap,
  isLoadingLogs,
  isMobile = false,
  filterBar,
  onSelectLog,
}) => {
  return (
    <Card bodyStyle={{ padding: isMobile ? '12px 8px' : '20px' }}>
      {filterBar}
      {isLoadingLogs ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin />
        </div>
      ) : (
        <Timeline
          mode="left"
          style={{ padding: isMobile ? '4px 0' : undefined }}
          items={
            logs.length === 0
              ? [
                  {
                    color: 'gray',
                    children:
                      'Ciclo Iniciado - Sin eventos registrados en el periodo seleccionado',
                  },
                ]
              : logs.map((log: any) => ({
                  key: log.id,
                  color: getLogColor(log.log_type),
                  dot: getLogIcon(log.log_type),
                  label: isMobile ? undefined : formatDateTime(log.log_date),
                  children: (
                    <div
                      style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {isMobile && (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#8c8c8c',
                            marginBottom: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <ClockCircleOutlined style={{ fontSize: 11 }} />
                          <span>{formatDateTime(log.log_date)}</span>
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: '6px',
                        }}
                      >
                        <div
                          style={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            wordBreak: 'break-word',
                          }}
                        >
                          <strong style={{ fontSize: isMobile ? 13 : 14 }}>
                            {getLogTypeLabel(log.log_type)}
                          </strong>

                          {log.production_cycle_stages?.name && (
                            <Tag
                              style={{
                                marginLeft: 6,
                                marginTop: 2,
                                maxWidth: '100%',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              {log.production_cycle_stages.name}
                            </Tag>
                          )}
                        </div>

                        <Button
                          type="link"
                          size="small"
                          icon={<InfoCircleOutlined />}
                          style={{
                            padding: 0,
                            height: 'auto',
                            fontSize: isMobile ? 12 : 13,
                            flexShrink: 0,
                          }}
                          onClick={() => onSelectLog(log)}
                        >
                          Ver Detalle
                        </Button>
                      </div>

                      {log.notes && (
                        <div
                          style={{
                            marginTop: 6,
                            fontStyle: 'italic',
                            wordBreak: 'break-word',
                            fontSize: isMobile ? 12 : 13,
                            color: '#595959',
                          }}
                        >
                          {log.notes}
                        </div>
                      )}

                      {log.quantity_change && (
                        <div
                          style={{
                            marginTop: 4,
                            fontWeight: 'bold',
                            fontSize: isMobile ? 12 : 13,
                            color: log.quantity_change > 0 ? 'green' : 'red',
                            wordBreak: 'break-word',
                          }}
                        >
                          Cambio en Capacidad: {log.quantity_change > 0 ? '+' : ''}
                          {log.quantity_change}
                        </div>
                      )}

                      {/* Elementos de Storage aplicados o comprados */}
                      {log.production_cycle_log_inputs &&
                        log.production_cycle_log_inputs.length > 0 && (
                          <div
                            style={{
                              marginTop: 6,
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 4,
                            }}
                          >
                            {log.production_cycle_log_inputs.map((inp: any) => {
                              const info = getResolvedElementInfo(inp, itemsMap);
                              const isServ = info.isService;
                              const isPurchase =
                                log.log_type === 'SUPPLY_PURCHASE';
                              const isLabor = log.log_type === 'LABOR_LOG';
                              const isExpense =
                                log.log_type === 'SERVICE_EXPENSE';
                              const tagColor = isPurchase
                                ? 'orange'
                                : isLabor
                                ? 'purple'
                                : isExpense
                                ? 'cyan'
                                : isServ
                                ? 'geekblue'
                                : 'blue';
                              const tagIcon = isPurchase
                                ? '🛒 '
                                : isLabor
                                ? '🧑‍🌾 '
                                : isExpense
                                ? '🚚 '
                                : isServ
                                ? '🔧 '
                                : '';
                              return (
                                <Tag
                                  key={inp.id}
                                  color={tagColor}
                                  style={{
                                    fontSize: 11,
                                    margin: 0,
                                    maxWidth: '100%',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    lineHeight: '18px',
                                    padding: '2px 6px',
                                    display: 'inline-flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 2,
                                  }}
                                >
                                  <span>
                                    {tagIcon}
                                    {info.name}:
                                  </span>
                                  <b>
                                    {isPurchase
                                      ? `+${inp.quantity}`
                                      : inp.quantity}{' '}
                                    {info.measureUnit}
                                  </b>
                                </Tag>
                              );
                            })}
                          </div>
                        )}

                      {/* Salidas o cosechas */}
                      {log.production_cycle_log_outputs &&
                        log.production_cycle_log_outputs.length > 0 && (
                          <div
                            style={{
                              marginTop: 6,
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 4,
                            }}
                          >
                            {log.production_cycle_log_outputs.map((out: any) => {
                              const info = getResolvedElementInfo(out, itemsMap);
                              return (
                                <Tag
                                  key={out.id}
                                  color="green"
                                  style={{
                                    fontSize: 11,
                                    margin: 0,
                                    maxWidth: '100%',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    lineHeight: '18px',
                                    padding: '2px 6px',
                                    display: 'inline-flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 2,
                                  }}
                                >
                                  <span>{info.name}:</span>
                                  <b>
                                    +{out.quantity}{' '}
                                    {info.measureUnit ||
                                      cycle.unit_of_measure ||
                                      ''}
                                  </b>
                                </Tag>
                              );
                            })}
                          </div>
                        )}

                      {/* Métricas */}
                      {log.metrics &&
                        Object.keys(log.metrics).length > 0 && (
                          <div
                            style={{
                              marginTop: 6,
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 4,
                              alignItems: 'center',
                            }}
                          >
                            <b style={{ fontSize: isMobile ? 11 : 12 }}>
                              Métricas:{' '}
                            </b>
                            {Object.entries(log.metrics).map(([k, v]) => (
                              <Tag
                                color="cyan"
                                key={k}
                                style={{
                                  fontSize: 11,
                                  margin: 0,
                                  maxWidth: '100%',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  padding: '1px 6px',
                                }}
                              >
                                {k}: {String(v)}
                              </Tag>
                            ))}
                          </div>
                        )}

                      {/* Adjuntos del evento */}
                      {log.production_cycle_log_attachments &&
                        log.production_cycle_log_attachments.length > 0 && (
                          <div
                            style={{
                              marginTop: 8,
                              paddingTop: 8,
                              borderTop: '1px solid #f0f0f0',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                marginBottom: 6,
                              }}
                            >
                              <PaperClipOutlined
                                style={{ color: '#888', fontSize: 12 }}
                              />
                              <span style={{ fontSize: 12, color: '#888' }}>
                                Adjuntos:
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 8,
                              }}
                            >
                              {(
                                log.production_cycle_log_attachments as AttachmentItem[]
                              ).map((att) => (
                                <Tooltip key={att.id} title={att.file_name}>
                                  <span style={{ display: 'inline-block' }}>
                                    {att.file_type === 'image' ? (
                                      <Image
                                        src={att.file_url}
                                        width={44}
                                        height={44}
                                        style={{
                                          objectFit: 'cover',
                                          borderRadius: 4,
                                          border: '1px solid #d9d9d9',
                                          cursor: 'pointer',
                                        }}
                                        preview={{ src: att.file_url }}
                                      />
                                    ) : (
                                      <a
                                        href={att.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          width: 44,
                                          height: 44,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '1px solid #ffccc7',
                                          borderRadius: 4,
                                          background: '#fff2f0',
                                          cursor: 'pointer',
                                          color: 'inherit',
                                          textDecoration: 'none',
                                        }}
                                      >
                                        <FilePdfOutlined
                                          style={{
                                            color: '#f5222d',
                                            fontSize: 18,
                                          }}
                                        />
                                        <span
                                          style={{
                                            fontSize: 9,
                                            color: '#888',
                                            marginTop: 1,
                                          }}
                                        >
                                          PDF
                                        </span>
                                      </a>
                                    )}
                                  </span>
                                </Tooltip>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  ),
                }))
          }
        />
      )}
    </Card>
  );
};
